import type { ProjectFormData } from '../interfaces/ui/addProjectModal';

export const isDuplicateProjectName = (
  projectName: string,
  existingProjectNames: string[],
): boolean => {
  const normalizedProjectName = projectName.trim().toLowerCase();

  return existingProjectNames.some(
    existingProjectName =>
      existingProjectName.trim().toLowerCase() === normalizedProjectName,
  );
};

export const useFormValidation = () => {
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateField = (
    fieldName: string,
    value: string | boolean,
  ): string => {
    switch (fieldName) {
      case 'projectName':
        return !String(value).trim() ? 'Project name is required' : '';

      case 'appUrl':
        if (!String(value).trim()) return 'Non-Prod App URL is required';
        if (!isValidUrl(String(value)))
          return 'Enter a valid URL. Example: https://dev.devx.cbre.com';
        return '';

      case 'environment':
        return !value ? 'Select an environment' : '';

      case 'projectMappingType':
        return !value ? 'Select a project mapping type' : '';

      case 'mappedProjectName':
        return !value ? 'Select a mapped project' : '';

      case 'testPlanName':
        return !value ? 'Select a test plan' : '';

      case 'testSuiteName':
        return !value ? 'Select a test suite' : '';

      case 'playwrightTestRepositoryUrl':
        if (!String(value).trim()) return '';
        return isValidUrl(String(value)) ? '' : 'Enter a valid URL';

      case 'flowId':
        return !value ? 'Select a flow' : '';

      default:
        return '';
    }
  };

  const validateForm = (
    formData: ProjectFormData,
    acknowledgementChecked: boolean,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Validate each field
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'mappedProjectName' && !formData.projectMappingType) {
        return;
      }

      if (key === 'testPlanName' && !formData.mappedProjectName) {
        return;
      }

      if (key === 'testSuiteName  ' && !formData.testPlanName) {
        return;
      }

      const error = validateField(key, value as string);
      if (error) {
        errors[key] = error;
      }
    });

    // Validate acknowledgement
    if (!formData.loginNotRequired && !acknowledgementChecked) {
      errors.acknowledgement = 'You must acknowledge the terms to proceed';
    }

    return errors;
  };

  const isFormValid = (
    formData: ProjectFormData,
    acknowledgementChecked: boolean,
  ): boolean => {
    const hasRequiredFields =
      formData.projectName?.trim() !== '' &&
      formData.appUrl?.trim() !== '' &&
      isValidUrl(formData.appUrl) &&
      formData.environment?.trim() !== '' &&
      formData.projectMappingType?.trim() !== '' &&
      formData.mappedProjectName?.trim() !== '' &&
      formData.testPlanName?.trim() !== '' &&
      formData.testSuiteName?.trim() !== '' &&
      (!formData.playwrightTestRepositoryUrl?.trim() ||
        isValidUrl(formData.playwrightTestRepositoryUrl));

    return (
      hasRequiredFields &&
      (formData.loginNotRequired || acknowledgementChecked)
    );
  };

  return { validateField, validateForm, isFormValid };
};
